import { type EnvironmentId, type ThreadId } from "@cmmd-forge/contracts";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTheme } from "../../hooks/useTheme";
import { useSettings } from "../../hooks/useSettings";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { useComposerDraftStore } from "../../composerDraftStore";
import { type DraftId } from "../../draftId";
import { parseDiffRouteSearch } from "../../diffRouteSearch";
import {
  LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
  LastInvokedScriptByProjectSchema,
} from "../ChatView.logic";
import { useChatComposerBindings } from "./useChatComposerBindings";
import { useChatImagePreviewState } from "./useChatImagePreviewState";
import { useChatRouteBootstrap } from "./useChatRouteBootstrap";

export type ChatRouteRuntimeProps =
  | {
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
      readonly routeKind: "server";
      readonly draftId?: never;
    }
  | {
      readonly environmentId: EnvironmentId;
      readonly threadId: ThreadId;
      readonly routeKind: "draft";
      readonly draftId: DraftId;
    };

export function useChatRouteRuntime(props: ChatRouteRuntimeProps) {
  const route = useChatRouteModel(props);
  const settings = useSettings();
  const bootstrap = useChatRouteBootstrap({
    environmentId: props.environmentId,
    threadId: props.threadId,
    routeKind: props.routeKind,
    draftId: route.draftId,
    draftRouteId: route.draftId,
    preferredModelSelection: settings.textGenerationModelSelection,
  });
  const [isRevertingCheckpoint, setIsRevertingCheckpoint] = useState(false);
  const [, setLastInvokedScriptByProjectId] = useLocalStorage(
    LAST_INVOKED_SCRIPT_BY_PROJECT_KEY,
    {},
    LastInvokedScriptByProjectSchema,
  );

  // Hooks must be called unconditionally at the top level, never inside the
  // returned object literal — calling them there made hook order fragile under
  // re-render ("Rendered fewer hooks than expected").
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false, select: parseDiffRouteSearch });
  const theme = useTheme();
  const composer = useChatComposerBindings();
  const imagePreview = useChatImagePreviewState();
  const stickyModelSelection = useComposerDraftStore((store) => store.setStickyModelSelection);
  const sendInFlightRef = useRef(false);

  return {
    route,
    settings,
    navigate,
    rawSearch,
    theme,
    bootstrap,
    composer,
    imagePreview,
    stickyModelSelection,
    runtimeFlags: {
      // isConnecting: WS reconnect state is not yet wired to this surface.
      // Hardcoding false (vs. a mutable useState) is honest about the current
      // implementation and avoids a setter that can never be called.
      isConnecting: false as const,
      isRevertingCheckpoint,
      setIsRevertingCheckpoint,
      sendInFlightRef,
      setLastInvokedScriptByProjectId,
    },
  };
}

function useChatRouteModel(props: ChatRouteRuntimeProps) {
  return {
    environmentId: props.environmentId,
    threadId: props.threadId,
    routeKind: props.routeKind,
    draftId: props.routeKind === "draft" ? props.draftId : null,
  };
}
